/*
 * Proprietary and confidential | 2023 | SoundTalks NV
 */

import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useLocation, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';

import { FluidPreloader } from 'components/core/preloader';
import { PrimaryButton } from 'ui';
import { actions } from 'data';

import s from './wechat-invite.module.scss';

const WechatInvite = ({ wechatInviteLogin }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();

  useEffect(() => {
    const handleInvite = () => {
      try {
        // Parse URL parameters
        const urlParams = new URLSearchParams(location.search);
        const inviteKey = urlParams.get('key');

        // Validate required parameters
        if (!inviteKey) {
          throw new Error(t('invalid_invitation_link'));
        }

        setIsLoading(true);

        // Redirect to WeChat login with invitation key
        wechatInviteLogin(inviteKey);
      } catch (error) {
        console.error('WeChat invite error:', error);
        setError(error.message);
        setIsLoading(false);
      }
    };

    handleInvite();
  }, [location, wechatInviteLogin, t]);

  const handleGoToLogin = () => {
    navigate('/sign-in');
  };

  if (isLoading) {
    return (
      <div className={s.wechatInvite}>
        <div className={s.wechatInviteContent}>
          <FluidPreloader />
          <p className={s.wechatInviteMessage}>{t('redirecting_to_wechat_login')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={s.wechatInvite}>
        <div className={s.wechatInviteContent}>
          <div className={s.wechatInviteError}>
            <h2>{t('wechat_invite_error')}</h2>
            <p>{error}</p>
            <div className={s.wechatInviteActions}>
              <PrimaryButton onClick={handleGoToLogin}>{t('go_to_login')}</PrimaryButton>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

WechatInvite.propTypes = {
  wechatInviteLogin: PropTypes.func.isRequired,
};

const mapDispatchToProps = {
  wechatInviteLogin: actions.wechat.wechatInviteLogin,
};

export default connect(null, mapDispatchToProps)(WechatInvite);
